import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './LandingPage.css';

const LandingPage = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  const dashboardPath = isAdmin ? '/admin' : '/dashboard';
  const pageRef = useRef(null);

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('visible'); observer.unobserve(e.target); } }),
      { threshold: 0.15 }
    );
    el.querySelectorAll('.scroll-reveal').forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page" ref={pageRef}>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg" aria-hidden="true">
          {/* NYC Skyline silhouette */}
          <svg className="hero-skyline" viewBox="0 0 1200 200" preserveAspectRatio="none" fill="none">
            <path d="M0,200 V160 H20 V130 H35 V100 H45 V80 H50 V60 H55 V80 H60 V100 H75 V120 H100 V90 H110 V70 H115 V50 H120 V30 H125 V50 H130 V70 H140 V100 H160 V130 H200 V110 H215 V80 H220 V55 H225 V35 H228 V20 H232 V35 H235 V55 H240 V80 H250 V110 H270 V140 H310 V120 H330 V90 H340 V65 H345 V45 H350 V30 H355 V45 H360 V65 H365 V90 H380 V125 H420 V105 H435 V75 H440 V50 H445 V25 H448 V12 H452 V25 H455 V50 H460 V75 H470 V105 H490 V130 H530 V110 H545 V85 H555 V60 H560 V40 H565 V60 H575 V90 H600 V120 H640 V100 H660 V70 H665 V45 H670 V25 H673 V15 H677 V25 H680 V45 H685 V70 H695 V100 H720 V130 H760 V110 H780 V80 H790 V55 H795 V35 H800 V55 H810 V85 H830 V115 H870 V95 H885 V65 H890 V40 H895 V20 H898 V8 H902 V20 H905 V40 H910 V65 H920 V95 H940 V120 H980 V100 H1000 V75 H1010 V50 H1015 V30 H1020 V50 H1030 V80 H1050 V110 H1080 V130 H1110 V105 H1125 V80 H1130 V55 H1135 V35 H1140 V55 H1150 V85 H1170 V120 H1200 V200 Z" fill="currentColor" />
          </svg>

          {/* Taxi 1 — foreground, drives right */}
          <svg className="hero-taxi taxi-1" viewBox="0 0 120 50" fill="none">
            <rect x="10" y="16" width="95" height="22" rx="4" fill="var(--taxi-yellow)" />
            <rect x="28" y="4" width="52" height="18" rx="4" fill="var(--taxi-yellow-dark)" />
            <rect x="32" y="7" width="15" height="10" rx="2" fill="var(--bg-primary)" opacity="0.35" />
            <rect x="51" y="7" width="15" height="10" rx="2" fill="var(--bg-primary)" opacity="0.35" />
            <rect x="70" y="7" width="8" height="10" rx="2" fill="var(--bg-primary)" opacity="0.25" />
            <circle cx="32" cy="42" r="8" fill="var(--text-muted)" />
            <circle cx="32" cy="42" r="4" fill="var(--bg-muted)" />
            <circle cx="88" cy="42" r="8" fill="var(--text-muted)" />
            <circle cx="88" cy="42" r="4" fill="var(--bg-muted)" />
            <rect x="0" y="24" width="10" height="4" rx="2" fill="var(--taxi-yellow-light)" opacity="0.7" />
            <rect x="105" y="26" width="10" height="3" rx="1.5" fill="var(--danger)" opacity="0.5" />
          </svg>

          {/* Taxi 2 — midground, drives left */}
          <svg className="hero-taxi taxi-2" viewBox="0 0 120 50" fill="none">
            <rect x="10" y="16" width="95" height="22" rx="4" fill="var(--taxi-yellow)" />
            <rect x="28" y="4" width="52" height="18" rx="4" fill="var(--taxi-yellow-dark)" />
            <rect x="32" y="7" width="15" height="10" rx="2" fill="var(--bg-primary)" opacity="0.35" />
            <rect x="51" y="7" width="15" height="10" rx="2" fill="var(--bg-primary)" opacity="0.35" />
            <circle cx="32" cy="42" r="8" fill="var(--text-muted)" />
            <circle cx="32" cy="42" r="4" fill="var(--bg-muted)" />
            <circle cx="88" cy="42" r="8" fill="var(--text-muted)" />
            <circle cx="88" cy="42" r="4" fill="var(--bg-muted)" />
          </svg>

          {/* Taxi 3 — background, drives right slowly */}
          <svg className="hero-taxi taxi-3" viewBox="0 0 120 50" fill="none">
            <rect x="10" y="16" width="95" height="22" rx="4" fill="var(--taxi-yellow)" />
            <rect x="28" y="4" width="52" height="18" rx="4" fill="var(--taxi-yellow-dark)" />
            <rect x="32" y="7" width="15" height="10" rx="2" fill="var(--bg-primary)" opacity="0.35" />
            <rect x="51" y="7" width="15" height="10" rx="2" fill="var(--bg-primary)" opacity="0.35" />
            <circle cx="32" cy="42" r="7" fill="var(--text-muted)" />
            <circle cx="32" cy="42" r="3.5" fill="var(--bg-muted)" />
            <circle cx="88" cy="42" r="7" fill="var(--text-muted)" />
            <circle cx="88" cy="42" r="3.5" fill="var(--bg-muted)" />
          </svg>
        </div>

        <div className="hero-content">
          <img src="/logo.png" alt="Taxify.nyc" className="hero-logo" />
          <h1 className="hero-title">
            Cars. Drivers. Medallions.<br />
            <span className="highlight">NYC's taxi marketplace.</span>
          </h1>
          <p className="hero-subtitle">
            One account, everything you need. Post a car, find a driver, lease or buy a medallion — direct contact, no middleman.
          </p>
          <div className="hero-buttons">
            {isAuthenticated ? (
              <Link to={dashboardPath} className="btn btn-primary">Go to Dashboard</Link>
            ) : (
              <>
                <Link to="/register" className="btn btn-primary">Get Started</Link>
                <Link to="/login" className="btn btn-secondary">Sign In</Link>
              </>
            )}
          </div>
          <div className="hero-tags">
            <span className="hero-tag yellow">Yellow Cab</span>
            <span className="hero-tag green">Green Boro</span>
            <span className="hero-tag black">Black Car</span>
            <span className="hero-tag medallion">Medallions</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <div className="feature scroll-reveal">
            <div className="feature-icon">01</div>
            <h3>Post in Seconds</h3>
            <p>List a car, post your availability as a driver, or offer a medallion for sale or lease — all from one account.</p>
          </div>
          <div className="feature scroll-reveal stagger-1">
            <div className="feature-icon">02</div>
            <h3>Browse & Match</h3>
            <p>Separate feeds for cars/drivers and medallions. Filter by type, shift, and more to find exactly what you need.</p>
          </div>
          <div className="feature scroll-reveal stagger-2">
            <div className="feature-icon">03</div>
            <h3>Connect Directly</h3>
            <p>Phone numbers and emails in every listing. Found a match? Reach out directly — no middleman, no booking system.</p>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="value-props">
        <div className="container">
          <div className="value-card drivers scroll-reveal">
            <div className="value-header">
              <span className="value-icon">C</span>
              <h3>Post a Car</h3>
            </div>
            <ul>
              <li>
                <strong>List your vehicle in seconds</strong>
                <span>Enter make, model, shift type, and rate — no need to save vehicles first</span>
              </li>
              <li>
                <strong>Set your own terms</strong>
                <span>Daily, weekly, or DOV shifts with negotiable or fixed rental rates</span>
              </li>
              <li>
                <strong>Get calls from drivers</strong>
                <span>Your phone number is right in the listing — drivers contact you directly</span>
              </li>
              <li>
                <strong>WAV & drive type support</strong>
                <span>Mark your vehicle as wheelchair accessible or specify hybrid/electric</span>
              </li>
              <li>
                <strong>Manage up to 5 active posts</strong>
                <span>Post multiple vehicles with your own location details</span>
              </li>
            </ul>
          </div>
          <div className="value-card fleets scroll-reveal stagger-1">
            <div className="value-header">
              <span className="value-icon">D</span>
              <h3>Post as a Driver</h3>
            </div>
            <ul>
              <li>
                <strong>Show your availability</strong>
                <span>Post your preferred shift types, work types, and drive type preferences</span>
              </li>
              <li>
                <strong>Get found by car owners</strong>
                <span>Owners browse driver listings and reach out when you're a good fit</span>
              </li>
              <li>
                <strong>Highlight your experience</strong>
                <span>TLC license, years of experience, and a bio to stand out</span>
              </li>
              <li>
                <strong>Browse available cars</strong>
                <span>Filter car listings by work type, shift schedule, and location</span>
              </li>
            </ul>
          </div>
          <div className="value-card medallions scroll-reveal stagger-2">
            <div className="value-header">
              <span className="value-icon">M</span>
              <h3>Medallions</h3>
            </div>
            <ul>
              <li>
                <strong>Buy, sell, or lease medallions</strong>
                <span>List your medallion for sale or lease, or post that you're looking for one</span>
              </li>
              <li>
                <strong>Yellow cab & green boro</strong>
                <span>Dedicated marketplace for NYC taxi medallion transactions</span>
              </li>
              <li>
                <strong>Set your price and terms</strong>
                <span>Weekly, monthly, or yearly lease rates — or set a sale price</span>
              </li>
              <li>
                <strong>Include vehicle details</strong>
                <span>Attach vehicle info, insurance status, and management company</span>
              </li>
            </ul>
          </div>
        </div>
      </section>


      {/* Footer */}
      <footer className="footer scroll-reveal">
        <div className="container">
          <div className="footer-content">
            <span className="footer-logo">Taxify.nyc</span>
            {!isAuthenticated ? (
              <div className="footer-links">
                <Link to="/login">Login</Link>
                <Link to="/register">Register</Link>
              </div>
            ) : (
              <Link to={dashboardPath} className="footer-link">Dashboard</Link>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
